import { createSelector } from '@reduxjs/toolkit';

import { advancementTypes } from '@shared';
import { boyScoutAdvancementTypes } from '@unit/constants';
import { eligibleAdvancementTypesSel } from '@unit/duck/selectors';

import { eagleRankId } from '../../constants';
import {
  additionalAvailableAdvancementsSel,
  availableAdvancementsSel,
} from './availableAdvancements.selectors';
import {
  additionalAdvancementIdSel,
  additionalAdvancementTypeSel,
  additionalAdvancementVerificationLoadingSel,
  additionalCompletionDateSel,
  additionalVerifyTimeoutErrorSel,
  advancementIdSel,
  advancementTypeSel,
  advancementVerificationLoadingSel,
  completionDateSel,
  selectedAdditionalAdvStatusSel,
  selectedAdditionalSuggestedYouthIdsSel,
  selectedAdvancementStatusSel,
  selectedSuggestedYouthIdsSel,
  verifyTimeoutErrorSel,
} from './core.selectors';
import {
  additionalEligiblePersonsSel,
  additionalSuggestedWithWarningsSel,
  additionalpersonsWithWarningsSel,
  eligiblePersonsSel,
  personsWithWarningsSel,
  suggestedWithWarningsSel,
} from './eligiblePersons.selectors';
import {
  isAdditionalTabActiveSel,
  suggestedAdditionalVerifiedYouthSel,
  suggestedVerifiedYouthSel,
} from './general.selectors';

const isEagleRank = ({ advancementType, advancementId }) =>
  advancementType === advancementTypes.RANKS && advancementId == eagleRankId;

export const isEagleRankSelectedSel = createSelector(
  advancementTypeSel,
  advancementIdSel,
  (advancementType, advancementId) =>
    isEagleRank({ advancementId, advancementType }),
);

export const isAdditionalEagleRankSelectedSel = createSelector(
  additionalAdvancementTypeSel,
  additionalAdvancementIdSel,
  (advancementType, advancementId) =>
    isEagleRank({ advancementType, advancementId }),
);

export const primaryTabDataSel = createSelector(
  completionDateSel,
  advancementTypeSel,
  advancementIdSel,
  availableAdvancementsSel,
  isEagleRankSelectedSel,
  advancementVerificationLoadingSel,
  verifyTimeoutErrorSel,
  eligiblePersonsSel,
  suggestedVerifiedYouthSel,
  selectedSuggestedYouthIdsSel,
  personsWithWarningsSel,
  suggestedWithWarningsSel,
  eligibleAdvancementTypesSel,
  selectedAdvancementStatusSel,
  (
    completionDate,
    advancementType,
    advancementId,
    availableAdvancements,
    eagleRankSelected,
    advancementVerificationLoading,
    verifyTimeoutError,
    eligiblePersons,
    suggestedYouth,
    selectedSuggestedYouthIds,
    personsWithWarnings,
    suggestedWithWarnings,
    eligibleAdvancementTypes,
    selectedAdvancementStatus,
  ) => ({
    completionDate,
    advancementType,
    advancementId,
    availableAdvancements,
    eagleRankSelected,
    advancementVerificationLoading,
    verifyTimeoutError,
    eligiblePersons,
    suggestedYouth,
    selectedSuggestedYouthIds,
    personsWithWarnings,
    suggestedWithWarnings,
    eligibleAdvancementTypes,
    selectedAdvancementStatus,
  }),
);

export const additionalTabDataSel = createSelector(
  additionalCompletionDateSel,
  additionalAdvancementTypeSel,
  additionalAdvancementIdSel,
  additionalAvailableAdvancementsSel,
  isAdditionalEagleRankSelectedSel,
  additionalAdvancementVerificationLoadingSel,
  additionalVerifyTimeoutErrorSel,
  additionalEligiblePersonsSel,
  suggestedAdditionalVerifiedYouthSel,
  selectedAdditionalSuggestedYouthIdsSel,
  additionalpersonsWithWarningsSel,
  additionalSuggestedWithWarningsSel,
  selectedAdditionalAdvStatusSel,
  (
    completionDate,
    advancementType,
    advancementId,
    availableAdvancements,
    eagleRankSelected,
    advancementVerificationLoading,
    verifyTimeoutError,
    eligiblePersons,
    suggestedYouth,
    selectedSuggestedYouthIds,
    personsWithWarnings,
    suggestedWithWarnings,
    selectedAdvancementStatus,
  ) => ({
    completionDate,
    advancementType,
    advancementId,
    availableAdvancements,
    eagleRankSelected,
    advancementVerificationLoading,
    verifyTimeoutError,
    eligiblePersons,
    suggestedYouth,
    selectedSuggestedYouthIds,
    personsWithWarnings,
    suggestedWithWarnings,
    eligibleAdvancementTypes: boyScoutAdvancementTypes,
    selectedAdvancementStatus,
  }),
);

export const activeTabDataSel = createSelector(
  isAdditionalTabActiveSel,
  primaryTabDataSel,
  additionalTabDataSel,
  (isAdditionalTabActive, primaryTabData, additionalTabData) =>
    isAdditionalTabActive ? additionalTabData : primaryTabData,
);
